// /home/labdoo/zitech-nexus/frontend/src/pages/ServiceRequestDetail.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { serviceAPI } from '../services/api';
import './ServiceRequestDetail.css';

interface ServiceRequestDetailData {
  id: number;
  service_name: string;
  service_name_display?: string;
  status: string;
  created_at: string;
  updated_at: string;
  project_details: string;
  company_name: string;
  selected_technologies: string[];
  user_email: string;
  user_name: string;
  attachments?: any[];
}

const ServiceRequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState<ServiceRequestDetailData | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedPdf, setSelectedPdf] = useState<string | null>(null);

  const fetchRequestDetail = useCallback(async () => {
    try {
      const response = await serviceAPI.getServiceRequest(parseInt(id!));
      console.log('Request detail:', response.data);
      setRequest(response.data);
    } catch (error) {
      console.error('Error fetching request details:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRequestDetail();
  }, [fetchRequestDetail]);

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      pending: '#ffc107',
      in_review: '#17a2b8',
      approved: '#28a745',
      in_progress: '#007bff',
      completed: '#28a745',
      cancelled: '#dc3545',
    };
    return colors[status] || '#6c757d';
  };

  const getStatusText = (status: string): string => {
    const texts: Record<string, string> = {
      pending: 'Pending',
      in_review: 'In Review',
      approved: 'Approved',
      in_progress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    return texts[status] || status;
  };

  const getServiceDisplayName = (): string => {
    if (request?.service_name_display) {
      return request.service_name_display;
    }
    if (request?.service_name) {
      return request.service_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return 'Service Request';
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension || '')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          <path d="M8 8H16M8 12H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    }
    if (extension === 'pdf') {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 4H10L14 8V20H4V4Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          <path d="M8 12H12M8 16H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    }
    if (['doc', 'docx'].includes(extension || '')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 4H10L14 8V20H4V4Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          <path d="M8 12H12M8 16H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    }
    if (['xls', 'xlsx', 'csv'].includes(extension || '')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          <path d="M8 8H16M8 12H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    }
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 4H10L14 8V20H4V4Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
      </svg>
    );
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isImageFile = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension || '');
  };

  const isPdfFile = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    return extension === 'pdf';
  };

  const getFileUrl = (filePath: string) => {
    if (!filePath) return '';
    if (filePath.startsWith('http')) return filePath;
    return `http://localhost:8000${filePath}`;
  };

  const openImageViewer = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };

  const closeImageViewer = () => {
    setSelectedImage(null);
  };

  const openPdfViewer = (pdfUrl: string) => {
    setSelectedPdf(pdfUrl);
  };

  const closePdfViewer = () => {
    setSelectedPdf(null);
  };

  const handleCancelRequest = async () => {
    if (window.confirm('Are you sure you want to cancel this request?')) {
      try {
        alert('Request cancelled successfully');
        navigate('/dashboard/service-requests');
      } catch (error) {
        console.error('Error cancelling request:', error);
        alert('Failed to cancel request');
      }
    }
  };

  if (loading) {
    return (
      <div className="srd-loading">
        <div className="srd-spinner"></div>
        <p>Loading request details...</p>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="srd-error">
        <h2 className="srd-error-title">Request not found</h2>
        <button className="srd-error-btn" onClick={() => navigate('/dashboard/service-requests')}>Back to Requests</button>
      </div>
    );
  }

  return (
    <div className="srd-container">
      {/* Image Modal */}
      {selectedImage && (
        <div className="srd-modal" onClick={closeImageViewer}>
          <div className="srd-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="srd-modal-close" onClick={closeImageViewer}>×</button>
            <img src={selectedImage} alt="Full size" className="srd-modal-image" />
            <a href={selectedImage} download className="srd-modal-download">
              Download Image
            </a>
          </div>
        </div>
      )}

      {/* PDF Modal */}
      {selectedPdf && (
        <div className="srd-modal" onClick={closePdfViewer}>
          <div className="srd-modal-content srd-pdf-modal" onClick={(e) => e.stopPropagation()}>
            <button className="srd-modal-close" onClick={closePdfViewer}>×</button>
            <iframe 
              src={`${selectedPdf}#toolbar=1&navpanes=1&scrollbar=1`} 
              className="srd-pdf-viewer"
              title="PDF Viewer"
            />
            <a href={selectedPdf} download className="srd-modal-download">
              Download PDF
            </a>
          </div>
        </div>
      )}

      <div className="srd-header">
        <button className="srd-back-btn" onClick={() => navigate('/dashboard/service-requests')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          Back to Requests
        </button>
        <div className="srd-title-section">
          <h1 className="srd-title">{getServiceDisplayName()}</h1>
          <span className="srd-status" style={{ backgroundColor: getStatusColor(request.status) }}>
            {getStatusText(request.status)}
          </span>
        </div>
        <p className="srd-subtitle">Request ID: #{request.id}</p>
      </div>

      <div className="srd-content">
        <div className="srd-grid">
          <div className="srd-card srd-info-card">
            <h3 className="srd-card-title">Request Information</h3>
            <div className="srd-row">
              <label>Submitted By:</label>
              <p>{request.user_name}</p>
            </div>
            <div className="srd-row">
              <label>Email:</label>
              <p>{request.user_email}</p>
            </div>
            <div className="srd-row">
              <label>Company:</label>
              <p>{request.company_name || 'Not specified'}</p>
            </div>
            <div className="srd-row">
              <label>Submitted Date:</label>
              <p>{new Date(request.created_at).toLocaleDateString()}</p>
            </div>
            <div className="srd-row">
              <label>Last Updated:</label>
              <p>{new Date(request.updated_at).toLocaleDateString()}</p>
            </div>
          </div>

          <div className="srd-card srd-details-card">
            <h3 className="srd-card-title">Project Details</h3>
            <div className="srd-project-details">
              <div className="srd-project-content">
                {request.project_details.split('\n').map((paragraph, index) => (
                  paragraph.trim() && (
                    <p key={index} className="srd-project-paragraph">
                      {paragraph}
                    </p>
                  )
                ))}
              </div>
            </div>
          </div>

          {request.selected_technologies && request.selected_technologies.length > 0 && (
            <div className="srd-card srd-tech-card">
              <h3 className="srd-card-title">Technologies</h3>
              <div className="srd-tech-list">
                {request.selected_technologies.map((tech, index) => (
                  <span key={index} className="srd-tech-tag">{tech}</span>
                ))}
              </div>
            </div>
          )}

          {request.attachments && request.attachments.length > 0 && (
            <div className="srd-card srd-attachments-card">
              <h3 className="srd-card-title">
                Attachments ({request.attachments.length})
              </h3>
              <div className="srd-attachments">
                {request.attachments.map((attachment, index) => {
                  const fileUrl = getFileUrl(attachment.file);
                  const isImage = isImageFile(attachment.file_name);
                  const isPdf = isPdfFile(attachment.file_name);
                  const displayFileName = attachment.file_name.length > 50 
                    ? attachment.file_name.substring(0, 47) + '...' 
                    : attachment.file_name;
                  
                  return (
                    <div key={index} className="srd-attachment-item">
                      {isImage ? (
                        <div className="srd-image-preview">
                          <img 
                            src={fileUrl} 
                            alt={attachment.file_name}
                            className="srd-image-thumbnail"
                            onClick={() => openImageViewer(fileUrl)}
                          />
                          <div className="srd-image-overlay">
                            <button 
                              className="srd-view-btn"
                              onClick={() => openImageViewer(fileUrl)}
                            >
                              View Image
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="srd-file-item">
                          <span className="srd-file-icon">
                            {getFileIcon(attachment.file_name)}
                          </span>
                          <div className="srd-file-info">
                            <p className="srd-file-name" title={attachment.file_name}>
                              {displayFileName}
                            </p>
                            <p className="srd-file-size">{formatFileSize(attachment.file_size)}</p>
                          </div>
                          <div className="srd-file-actions">
                            {isPdf && (
                              <button 
                                className="srd-view-btn"
                                onClick={() => openPdfViewer(fileUrl)}
                              >
                                View PDF
                              </button>
                            )}
                            <a 
                              href={fileUrl} 
                              download
                              className="srd-download-btn"
                            >
                              Download
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="srd-actions">
          {request.status === 'pending' && (
            <button className="srd-cancel-btn" onClick={handleCancelRequest}>
              Cancel Request
            </button>
          )}
          <button className="srd-support-btn" onClick={() => navigate('/support')}>
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceRequestDetail;